import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { Observable, Subject } from 'rxjs'
import { AlertItem, AlertService } from '../../services/alert.service'
import { distinctUntilChanged, takeUntil } from 'rxjs/operators'

@Component({
  selector: 'app-alerts-editor',
  templateUrl: './alerts-editor.component.html',
  styleUrls: ['./alerts-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AlertsEditorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() awsOutageItem: AlertItem | undefined
  @Output() previewTextChange = new EventEmitter<string>()
  @Output() showBanner = new EventEmitter<boolean>()

  form: FormGroup
  categories: string[] = []
  categoryOptions: { label: string; value: string }[] = []
  data$: Observable<AlertItem[]> = this.alertService.getFilteredData()
  editMode = false
  currentCategory: string | null = null
  loading = false
  // showCategory = false;

  private destroy$ = new Subject<void>()
  private userInteractedWithToggle = false // Track user interaction with the toggle
  private userInteractedWithMessage = false // Track user interaction with the message

  constructor(
    private fb: FormBuilder,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      category: [''],
      visible: [false, Validators.required],
      message: [''],
    })

    this.form
      .get('message')
      ?.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.userInteractedWithMessage = true // User has typed in the message
        this.previewTextChange.emit(value)
      })

    // Listen to changes in the toggle button (visible control)
    this.form
      .get('visible')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((visible: boolean) => {
        this.userInteractedWithToggle = true // User has toggled the switch
        this.showBanner.emit(visible)
      })

    this.alertService
      .getCategories()
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((categories) => {
        this.categories = categories
        this.categoryOptions = this.categories.map((category) => ({ label: category, value: category }))
        this.updateForm() // Ensure form updates after categories are loaded
      })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.awsOutageItem && !changes.awsOutageItem.isFirstChange()) {
      this.updateForm()
    }
  }

  updateForm(): void {
    if (this.awsOutageItem) {
      const formUpdates: Partial<AlertItem> = {
        category: this.awsOutageItem.category || 'outage',
        visible: this.userInteractedWithToggle ? this.form.get('visible')?.value : this.awsOutageItem.visible,
      }

      if (!this.userInteractedWithMessage) {
        formUpdates.message = this.awsOutageItem.message
      }

      this.form.patchValue(formUpdates)
      this.editMode = true
      this.currentCategory = this.awsOutageItem.category
    } else {
      this.editMode = false
      if (this.categories.length > 0) {
        if (!this.form.get('category')?.value) {
          this.form.get('category')?.patchValue('outage') // Set 'outage' as default category
        }
        this.currentCategory = 'outage'
        this.form.get('category')?.markAsTouched()
      }
    }
  }

  onSubmit() {
    this.loading = true
    // Ensure default category is set to 'outage' if not provided
    if (!this.form.get('category')?.value) {
      this.form.get('category')?.setValue('outage')
    }
    const formData: AlertItem = this.form.value

    if (this.editMode) {
      this.alertService
        .updateAlerts(this.currentCategory!, formData)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.loading = false
        })
    } else {
      this.alertService
        .updateAlerts(this.currentCategory!, formData)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.alertService
            .getCategories()
            .pipe(takeUntil(this.destroy$))
            .subscribe((categories) => {
              this.categories = categories
              this.categoryOptions = this.categories.map((category) => ({ label: category, value: category }))
            })
          this.loading = false
        })
    }
  }

  resetForm() {
    this.form.reset()
    this.editMode = false
    this.currentCategory = null
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }
}
